// Calendly connection pill used on the AI Outbound campaign detail page (and
// anywhere else we want to expose the connect flow). Renders:
//   • RED when the caller has no Calendly connection
//   • GREEN when they do (shows connected email + disconnect option)
//
// The connect action navigates to /api/outbound/calendly/connect via a real
// browser redirect (not fetch) so the OAuth handshake round-trips through
// Calendly's origin and lands back at our /oauth-callback.

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, XCircle, Link as LinkIcon, LogOut, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

/**
 * @param {string} nextPath — optional URL path (starting with `/`) to bounce
 * the browser back to after the OAuth dance completes. Defaults to
 * `/ai-outbound`. Include search params in the path if you need them.
 */
export function CalendlyConnectPill({ nextPath = "/ai-outbound" }) {
  const [loading, setLoading] = useState(true);
  const [conn, setConn] = useState(null); // { connected: bool, calendly_email, ... }
  const [disconnecting, setDisconnecting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/outbound/calendly/status");
      setConn(data);
    } catch (e) {
      // 401/403 etc — treat as unauthenticated / not connected so pill still
      // renders and gives an actionable next step.
      setConn({ connected: false, error: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Also react to the ?calendly=connected|error query the callback appends.
    const params = new URLSearchParams(window.location.search);
    const state = params.get("calendly");
    if (state === "connected") {
      toast.success("Calendly connected");
      // Clean the URL so a hard refresh doesn't re-toast.
      params.delete("calendly");
      const clean = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState({}, "", clean);
    } else if (state === "error") {
      toast.error(`Calendly connect failed: ${params.get("reason") || "unknown"}`);
      params.delete("calendly"); params.delete("reason");
      const clean = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState({}, "", clean);
    }
  }, []);

  const connect = () => {
    // A full-page redirect, not fetch — OAuth flow needs to land at Calendly's
    // origin. We attach the caller's session cookie so the /connect endpoint
    // can identify them and sign a state token bound to their user_id.
    const url = `${process.env.REACT_APP_BACKEND_URL.replace(/\/+$/, "")}/api/outbound/calendly/connect?next=${encodeURIComponent(nextPath)}`;
    window.location.href = url;
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Calendly account? Bookings from active campaigns will stop landing on your calendar.")) return;
    setDisconnecting(true);
    try {
      await api.post("/outbound/calendly/disconnect");
      toast.success("Calendly disconnected");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Disconnect failed");
    } finally { setDisconnecting(false); }
  };

  if (loading) {
    return (
      <span
        data-testid="calendly-pill-loading"
        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-500"
      >
        <Loader2 className="h-3 w-3 animate-spin" /> Calendly…
      </span>
    );
  }

  // ------------ NOT CONNECTED (RED) ------------
  if (!conn?.connected) {
    return (
      <button
        type="button"
        onClick={connect}
        data-testid="calendly-pill-connect"
        title="Connect your Calendly account so booked meetings from AI campaigns land on your calendar"
        className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 shadow-sm transition-colors hover:bg-red-100 hover:border-red-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
      >
        <XCircle className="h-3.5 w-3.5" />
        Connect Calendly
      </button>
    );
  }

  // ------------ CONNECTED (GREEN) ------------
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="calendly-pill-connected"
          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 shadow-sm transition-colors hover:bg-emerald-100 hover:border-emerald-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          Calendly connected
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 text-sm">
        <div className="flex items-center gap-2 text-slate-800">
          <CalendarClock className="h-4 w-4 text-emerald-600" />
          <div className="min-w-0">
            <div className="truncate font-semibold" data-testid="calendly-pill-name">
              {conn.calendly_name || "Calendly user"}
            </div>
            <div className="truncate text-xs text-slate-500" data-testid="calendly-pill-email">
              {conn.calendly_email}
            </div>
          </div>
        </div>
        {conn.scheduling_url && (
          <a
            href={conn.scheduling_url}
            target="_blank"
            rel="noreferrer"
            data-testid="calendly-pill-scheduling-link"
            className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <LinkIcon className="h-3 w-3" />
            Open my Calendly booking page
          </a>
        )}
        <div className="mt-3 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
          Booked meetings from every campaign you own will be scheduled on this Calendly account.
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={disconnect}
          disabled={disconnecting}
          data-testid="calendly-pill-disconnect"
          className="mt-3 w-full border-slate-200 text-slate-700 hover:bg-slate-50"
        >
          {disconnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />}
          Disconnect
        </Button>
      </PopoverContent>
    </Popover>
  );
}
